import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  CalendarRange,
  Clock3,
  LoaderCircle,
  RefreshCw,
  TrendingUp,
} from "lucide-react";

import { getReportOverview } from "./api";
import type { ReportCount, ReportOverview } from "./types";

type DateRange = { from: string; to: string };

export function ReportsPage() {
  const [range, setRange] = useState<DateRange>(defaultRange);
  const [draft, setDraft] = useState<DateRange>(range);
  const [report, setReport] = useState<ReportOverview>();
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await getReportOverview(range.from, range.to));
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => void load(), [load]);

  function apply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!draft.from || !draft.to || draft.from > draft.to) return;
    setRange(draft);
  }

  function preset(days: number) {
    const next = rangeForDays(days);
    setDraft(next);
    setRange(next);
  }

  return (
    <div className="page-content reports-page">
      <header className="page-intro report-intro">
        <span className="eyebrow lime">Operational intelligence</span>
        <h1>A clearer view of demand, conversion, and capacity.</h1>
        <p>
          Follow the customer journey from inquiry to scheduled service using live TLC operational
          data. Dates and appointment workload follow Massachusetts time.
        </p>
      </header>

      <section className="report-controls" aria-label="Report period">
        <div className="report-presets" aria-label="Quick date ranges">
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              className={report?.range.days === days ? "active" : ""}
              onClick={() => preset(days)}
            >
              {days} days
            </button>
          ))}
        </div>
        <form className="report-date-form" onSubmit={apply}>
          <label>
            From
            <input
              type="date"
              value={draft.from}
              max={draft.to || businessToday()}
              onChange={(event) =>
                setDraft((current) => ({ ...current, from: event.target.value }))
              }
              required
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={draft.to}
              min={draft.from}
              max={businessToday()}
              onChange={(event) => setDraft((current) => ({ ...current, to: event.target.value }))}
              required
            />
          </label>
          <button className="secondary-button report-apply" disabled={loading}>
            <CalendarRange /> Apply
          </button>
          <button
            className="icon-button report-refresh"
            type="button"
            aria-label="Refresh report"
            onClick={() => void load()}
            disabled={loading}
          >
            {loading ? <LoaderCircle className="spin" /> : <RefreshCw />}
          </button>
        </form>
      </section>

      {failed ? (
        <div className="load-error" role="alert">
          We couldn’t refresh operational reporting.
          <button onClick={() => void load()}>Try again</button>
        </div>
      ) : null}

      <section className="report-summary" aria-label="Report summary">
        <SummaryCard
          label="Leads captured"
          value={report?.summary.leads}
          detail="New inquiries"
          tone="forest"
        />
        <SummaryCard
          label="Booking requests"
          value={report?.summary.bookingRequests}
          detail="Requests created"
        />
        <SummaryCard
          label="Scheduled visits"
          value={report?.summary.appointments}
          detail={
            report
              ? `${report.summary.completedAppointments} completed · ${formatHours(report.summary.scheduledMinutes)}`
              : "Excludes cancelled"
          }
        />
        <SummaryCard
          label="Lead conversion"
          value={report ? `${formatNumber(report.summary.leadToAppointmentRate)}%` : undefined}
          detail={`${report?.summary.convertedLeads ?? "—"} leads scheduled`}
          tone="lime"
        />
      </section>

      <section className="content-card report-trend-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Daily activity</span>
            <h2>Demand and scheduling trend</h2>
          </div>
          <span className="report-period">
            <CalendarRange /> {report ? formatPeriod(report.range) : "Loading period…"}
          </span>
        </div>
        {loading && !report ? <ReportLoading /> : report ? <DailyChart report={report} /> : null}
      </section>

      <section className="report-detail-grid">
        <BreakdownCard
          eyebrow="Acquisition"
          title="Lead sources"
          items={report?.leadSources ?? []}
          empty="Source data will appear as inquiries arrive."
        />
        <BreakdownCard
          eyebrow="Pipeline"
          title="Lead status"
          items={report?.leadStatuses ?? []}
          empty="Lead pipeline activity will appear here."
        />
        <BreakdownCard
          eyebrow="Scheduling"
          title="Visit status"
          items={report?.appointmentStatuses ?? []}
          empty="Appointment status activity will appear here."
        />
        <ServiceCard report={report} />
        <TeamCard report={report} />
      </section>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value?: number | string;
  detail: string;
  tone?: "forest" | "lime";
}) {
  return (
    <article className={`report-summary-card ${tone ?? ""}`}>
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
      <small>{detail}</small>
    </article>
  );
}

function DailyChart({ report }: { report: ReportOverview }) {
  const max = useMemo(
    () =>
      Math.max(
        1,
        ...report.daily.flatMap((day) => [day.leads, day.bookingRequests, day.appointments]),
      ),
    [report.daily],
  );

  if (report.daily.every((day) => day.leads + day.bookingRequests + day.appointments === 0)) {
    return <EmptyReport copy="No lead, booking, or appointment activity in this period." />;
  }

  return (
    <>
      <div className="report-legend" aria-hidden="true">
        <span className="lead">Leads</span>
        <span className="request">Requests</span>
        <span className="appointment">Appointments</span>
      </div>
      <div
        className="report-chart-scroll"
        tabIndex={0}
        aria-label="Scrollable daily activity chart"
      >
        <div
          className="report-chart"
          style={{ minWidth: `${Math.max(720, report.daily.length * 24)}px` }}
        >
          {report.daily.map((day, index) => (
            <div className="report-day" key={day.date}>
              <div
                className="report-bars"
                title={`${formatShortDate(day.date)}: ${day.leads} leads, ${day.bookingRequests} requests, ${day.appointments} appointments`}
              >
                <i className="lead" style={barHeight(day.leads, max)} />
                <i className="request" style={barHeight(day.bookingRequests, max)} />
                <i className="appointment" style={barHeight(day.appointments, max)} />
              </div>
              <time dateTime={day.date}>
                {showDateLabel(index, report.daily.length) ? formatAxisDate(day.date) : ""}
              </time>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function BreakdownCard({
  eyebrow,
  title,
  items,
  empty,
}: {
  eyebrow: string;
  title: string;
  items: ReportCount[];
  empty: string;
}) {
  const max = Math.max(1, ...items.map((item) => item.count));
  return (
    <article className="content-card report-breakdown-card">
      <header>
        <span className="eyebrow">{eyebrow}</span>
        <h2>{title}</h2>
      </header>
      {items.length === 0 ? (
        <EmptyReport copy={empty} />
      ) : (
        <div className="report-ranking">
          {items.map((item) => (
            <div key={item.key}>
              <span>{humanize(item.label)}</span>
              <strong>{item.count}</strong>
              <i aria-hidden="true">
                <b style={{ width: `${(item.count / max) * 100}%` }} />
              </i>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function ServiceCard({ report }: { report?: ReportOverview }) {
  return (
    <article className="content-card report-breakdown-card">
      <header>
        <span className="eyebrow">Service mix</span>
        <h2>Requested services</h2>
      </header>
      {!report?.services.length ? (
        <EmptyReport copy="Service demand will appear as requests are qualified." />
      ) : (
        <div className="report-record-list">
          {report.services.map((service) => (
            <div key={service.id}>
              <span>
                <strong>{service.name}</strong>
                <small>Configured service</small>
              </span>
              <span>
                <b>{service.bookingRequests}</b>
                <small>requests</small>
              </span>
              <span>
                <b>{service.appointments}</b>
                <small>visits</small>
              </span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function TeamCard({ report }: { report?: ReportOverview }) {
  return (
    <article className="content-card report-breakdown-card">
      <header>
        <span className="eyebrow">Capacity</span>
        <h2>Scheduled team workload</h2>
      </header>
      {!report?.teams.length ? (
        <EmptyReport copy="Team workload will appear after visits are scheduled." />
      ) : (
        <div className="report-record-list">
          {report.teams.map((team) => (
            <div key={team.id ?? "unassigned"}>
              <span>
                <strong>{team.name}</strong>
                <small>{team.appointments} scheduled visits</small>
              </span>
              <span className="report-hours">
                <Clock3 />
                <b>{formatHours(team.scheduledMinutes)}</b>
              </span>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function EmptyReport({ copy }: { copy: string }) {
  return (
    <div className="report-empty">
      <BarChart3 />
      <span>{copy}</span>
    </div>
  );
}

function ReportLoading() {
  return (
    <div className="report-loading">
      <LoaderCircle className="spin" /> Building live report…
    </div>
  );
}

function defaultRange() {
  return rangeForDays(30);
}

function rangeForDays(days: number): DateRange {
  const to = businessToday();
  const fromDate = new Date(`${to}T12:00:00Z`);
  fromDate.setUTCDate(fromDate.getUTCDate() - days + 1);
  return { from: fromDate.toISOString().slice(0, 10), to };
}

function businessToday() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function barHeight(value: number, max: number) {
  return { height: value === 0 ? 0 : `${Math.max(5, (value / max) * 100)}%` };
}

function showDateLabel(index: number, length: number) {
  const interval = length <= 14 ? 1 : length <= 45 ? 5 : length <= 100 ? 10 : 30;
  return index === 0 || index === length - 1 || index % interval === 0;
}

function formatPeriod(range: ReportOverview["range"]) {
  return `${formatShortDate(range.from)} – ${formatShortDate(range.to)} · ${range.days} days`;
}

function formatShortDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T00:00:00Z`));
}

function formatAxisDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T00:00:00Z`));
}

function humanize(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatHours(minutes: number) {
  return `${formatNumber(minutes / 60)} hr`;
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(value);
}
